<template>
  <div class="ps-date-picker">
    <div v-if="placeholder" class="pb-2">{{ $t(label) }}</div>
    <v-menu
      ref="menu"
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          v-model="text"
          :readonly="readonly"
          v-bind="attrs"
          v-on="on"
          :placeholder="$t(label)"
          :rounded="rounded"
          :flat="flat"
          :outlined="outlined"
          :dense="dense"
          :disabled="disabled"
          :hide-details="hideDetails"
          :error-messages="errorMessages"
          :counter="counter"
          :filled="filled"
          :clearable="clearable"
          single-line
          @click:clear="model = null"
          @blur="commitText"
          @keydown.enter.prevent="commitText"
        >
          <template v-slot:append>
            <v-icon small>fal fa-calendar-alt</v-icon>
          </template>
        </v-text-field>
      </template>
      <v-date-picker
        v-model="model"
        :min="minDate"
        no-title
        scrollable
        @input="menu = false"
      />
    </v-menu>
  </div>
</template>

<script>
export default {
  props: {
    value: {},
    placeholder: { type: Boolean, default: true },
    label: { type: String, require: true },
    flat: { type: Boolean, default: false },
    errorMessages: { type: Array, require: true },
    outlined: { type: Boolean, default: true },
    dense: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    counter: { type: Boolean, default: false },
    filled: { type: Boolean, default: false },
    rounded: { type: Boolean, default: false },
    readonly: { type: Boolean, default: true },
    clearable: { type: Boolean, default: false },
    // earliest selectable date (YYYY-MM-DD)
    minDate: { type: String, default: undefined },
  },
  data() {
    return {
      menu: false,
      model: this.value,
      text: this.formatFromYmd(this.value),
      lastValid: this.value,
    };
  },

  watch: {
    value(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.model = newVal;
        this.text = this.formatFromYmd(newVal);
        this.lastValid = newVal;
      }
    },
    model(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.text = this.formatFromYmd(newVal);
        this.lastValid = newVal;
        this.$emit("input", newVal);
      }
    },
  },
  computed: {
    hideDetails() {
      if (this.errorMessages && this.errorMessages.length) {
        return false;
      }
      return true;
    },
  },
  methods: {
    commitText() {
      if (this.readonly) return;
      const parsed = this.parseToYmd(this.text);
      if (parsed) {
        this.model = parsed;
        return;
      }
      // Revert to the last known valid date (or null)
      this.text = this.formatFromYmd(this.lastValid);
    },
    parseToYmd(val) {
      if (val === null || val === undefined) return null;
      const raw = String(val).trim();
      if (raw === "") return null;

      // Accept YYYY-MM-DD (Vuetify v-date-picker default)
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        return this.isValidYmd(raw) ? raw : null;
      }

      // Accept DD-MM-YYYY or DD/MM/YYYY (desired manual entry)
      const m = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
      if (m) {
        const day = m[1].padStart(2, "0");
        const month = m[2].padStart(2, "0");
        const year = m[3].length === 2 ? `20${m[3]}` : m[3];
        const ymd = `${year}-${month}-${day}`;
        return this.isValidYmd(ymd) ? ymd : null;
      }

      return null;
    },
    formatFromYmd(val) {
      if (!val) return val;
      const raw = String(val).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [y, m, d] = raw.split("-");
        return `${d}/${m}/${y}`;
      }
      // If already in dd/mm/yyyy, keep as-is
      if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(raw)) return raw;
      return raw;
    },
    isValidYmd(ymd) {
      const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
      if (!y || !m || !d) return false;
      if (m < 1 || m > 12) return false;
      if (d < 1 || d > 31) return false;
      const dt = new Date(Date.UTC(y, m - 1, d));
      return (
        dt.getUTCFullYear() === y &&
        dt.getUTCMonth() === m - 1 &&
        dt.getUTCDate() === d
      );
    },
  },
};
</script>

<style scoped>
.ps-date-picker ::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  align-self: center !important;
}
</style>
